import { ClbLoading } from '@celebration/react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadCardBody from '../../../../../common/components/load-card/load-card-body/load-card-body';
import LoadCardHeader from '../../../../../common/components/load-card/load-card-header/load-card-header';
import { IRouteDetails } from '../../../../../common/components/load-card/load-card-body/ILoadCardBody';
import { formatCurrency } from '../../../../../utils/currency-utils';
import IWonOfferCardProps from './IWonOfferCardProps';
import LoadCardFooter from '../../../../../common/components/load-card/load-card-footer/load-card-footer';
import WonOfferActionButtons from '../won-offer-action-buttons/won-offer-action-buttons';
import './won-offer-card.scss';
import RoundManagerApiService from '../../../../../common/services/roundmanager-api';
import { useToast } from '../../../../../app/contexts/toast/toast.context';
import { getWonOfferTagStatusType } from '../../utils/won-offer-utils';
import { EMPTY_FIELD } from '../../../../../utils/string-utils';
import { getUTCDate } from '../../../../../utils/date-utils';
import { getWonOfferCancellationReasonMessage } from '../../../../../common/utils/cancellation-reason-utils';

export default function WonOfferCard({
    wonOffer,
    updateOffer,
}: Readonly<IWonOfferCardProps>) {
    const { t } = useTranslation();
    const { setToast } = useToast();

    const [isLoading, setIsLoading] = useState(false);
    const weightLabel = `${wonOffer.load.shipment.weight} ${wonOffer.load.shipment.weightUnitMeasure}`;

    const reasonMessage = getWonOfferCancellationReasonMessage(
        wonOffer.cancellationReason,
        wonOffer.load.channel,
        wonOffer.status,
        t,
        EMPTY_FIELD,
    );

    const refreshOffer = useCallback(
        async (loadId: string) => {
            setIsLoading(true);
            const { response, error } =
                await RoundManagerApiService.Offers.GetWonOfferById(loadId);

            if (response) {
                updateOffer(response);
            } else {
                setToast('negative', t('Erro'), error?.message);
            }

            setIsLoading(false);
        },
        [updateOffer, setToast, t],
    );

    return (
        <div
            role="none"
            key={wonOffer.offerId}
            data-testid="won-offer-card"
            className="won-offer-card"
        >
            {isLoading ? (
                <ClbLoading />
            ) : (
                <div className="won-offer-card-main">
                    <LoadCardHeader
                        loadId={wonOffer.load.loadId ?? EMPTY_FIELD}
                        showLoadId
                        tag={getWonOfferTagStatusType(wonOffer)}
                        checkboxDisabled
                    />

                    <LoadCardBody
                        sourceRoute={
                            {
                                code: wonOffer.load.route.source?.code,
                                description:
                                    wonOffer.load.route.source?.description,
                                city: wonOffer.load.route.source?.city,
                                federativeUnit:
                                    wonOffer.load.route.source?.federativeUnit,
                            } as IRouteDetails
                        }
                        destinationRoute={
                            {
                                code: wonOffer.load.route.destination?.code,
                                description:
                                    wonOffer.load.route.destination
                                        ?.description,
                                city: wonOffer.load.route.destination?.city,
                                federativeUnit:
                                    wonOffer.load.route.destination
                                        ?.federativeUnit,
                            } as IRouteDetails
                        }
                        showReason={wonOffer.status === 'Canceled'}
                        reasonMessage={reasonMessage}
                        channel={wonOffer.load.channel ?? EMPTY_FIELD}
                        collectionDate={getUTCDate(
                            wonOffer.load.dates.collectionDate,
                        )}
                        weight={weightLabel}
                        price={formatCurrency(
                            wonOffer.load.shipment.chargeAmount!,
                            wonOffer.load.shipment.currency,
                        )}
                        skids={wonOffer.load.shipment.skids}
                        isReturnable={wonOffer.load.isReturnable}
                        isReprogrammed={wonOffer.load.isReprogrammed}
                    />

                    <LoadCardFooter
                        mainButtons={
                            <WonOfferActionButtons
                                offer={wonOffer}
                                refreshItem={() =>
                                    refreshOffer(wonOffer.offerId)
                                }
                            />
                        }
                        hiddenIconButtons
                    />
                </div>
            )}
        </div>
    );
}
